import React, { useRef, useEffect, useState, useCallback } from 'react';
import Hls from 'hls.js';

interface HLSVideoPlayerProps {
    /** HLS manifest (.m3u8) URL — preferred for adaptive streaming */
    hlsUrl?: string;
    /** Fallback direct video URL (used if HLS unavailable or not yet converted) */
    fallbackUrl: string;
    /** Poster/thumbnail image */
    poster?: string;
    /** CSS class for the video element */
    className?: string;
    /** Whether to show controls */
    controls?: boolean;
    /** Auto-play */
    autoPlay?: boolean;
    /** Muted (required for autoplay on most browsers) */
    muted?: boolean;
    /** Loop playback */
    loop?: boolean;
    /** Callback when video starts playing */
    onPlay?: () => void;
    /** Callback when video ends */
    onEnded?: () => void;
    /** Max height style */
    maxHeight?: string;
    /** Hide extra badges/quality controls for compact chat bubbles */
    compact?: boolean;
}

/**
 * HLS Video Player Component
 * 
 * Plays HLS adaptive-bitrate streams when available, with automatic
 * fallback to standard video for:
 * - Safari/iOS (native HLS support)
 * - Old browsers without MSE
 * - Videos not yet converted to HLS
 *
 * Features:
 * - Adaptive bitrate (auto quality switching based on bandwidth)
 * - Quality selector overlay
 * - Smooth error recovery
 * - Memory-safe cleanup
 */
export const HLSVideoPlayer: React.FC<HLSVideoPlayerProps> = ({
    hlsUrl,
    fallbackUrl,
    poster,
    className = '',
    controls = true,
    autoPlay = false,
    muted = false,
    loop = false,
    onPlay,
    onEnded,
    maxHeight,
    compact = false,
}) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const hlsRef = useRef<Hls | null>(null);
    const [isHlsActive, setIsHlsActive] = useState(false);
    const [preferFallback, setPreferFallback] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const destroyHls = useCallback(() => {
        if (hlsRef.current) {
            hlsRef.current.destroy();
            hlsRef.current = null;
        }
        setIsHlsActive(false);
    }, []);

    const switchToFallback = useCallback(() => {
        destroyHls();
        setPreferFallback(true);
        setError(null);
    }, [destroyHls]);

    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        video.setAttribute('playsinline', 'true');
        video.setAttribute('webkit-playsinline', 'true');
        video.setAttribute('x-webkit-airplay', 'deny');
        video.setAttribute('controlsList', 'nodownload nofullscreen noremoteplayback');
    }, []);

    useEffect(() => {
        setPreferFallback(false);
        setError(null);
    }, [hlsUrl, fallbackUrl]);

    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        // If we have an HLS URL and hls.js is supported, use it
        if (hlsUrl && !preferFallback && Hls.isSupported()) {
            const hls = new Hls({
                enableWorker: true,
                lowLatencyMode: false,
                // Start with lower quality for fast initial load
                startLevel: -1, // Auto
                capLevelToPlayerSize: true,
                // Recovery settings
                fragLoadingMaxRetry: 3,
                manifestLoadingMaxRetry: 3,
                levelLoadingMaxRetry: 3,
            });

            hlsRef.current = hls;
            hls.loadSource(hlsUrl);
            hls.attachMedia(video);

            hls.on(Hls.Events.MANIFEST_PARSED, () => {
                setIsHlsActive(true);
                setError(null);
            });

            hls.on(Hls.Events.ERROR, (_event, data) => {
                if (data.fatal) {
                    switch (data.type) {
                        case Hls.ErrorTypes.NETWORK_ERROR:
                            switchToFallback();
                            break;
                        case Hls.ErrorTypes.MEDIA_ERROR:
                            hls.recoverMediaError();
                            break;
                        default:
                            switchToFallback();
                            break;
                    }
                }
            });

            return () => {
                destroyHls();
            };
        }
        // Safari has native HLS support — just set the source directly
        else if (hlsUrl && !preferFallback && video.canPlayType('application/vnd.apple.mpegurl')) {
            video.src = hlsUrl;
            setIsHlsActive(true);

            const handleNativeError = () => {
                switchToFallback();
            };
            video.addEventListener('error', handleNativeError);

            return () => {
                video.removeEventListener('error', handleNativeError);
                video.src = '';
            };
        }
        // No HLS support or no HLS URL — use fallback direct video
        else {
            video.src = fallbackUrl;
            setIsHlsActive(false);

            return () => {
                video.src = '';
            };
        }
    }, [hlsUrl, fallbackUrl, destroyHls, preferFallback, switchToFallback]);

    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        if (autoPlay) {
            video.play().catch(() => { });
        } else {
            video.pause();
        }
    }, [autoPlay]);

    return (
        <div
            className={`hls-player-wrapper ${className}`}
            style={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                height: '100%',
                maxHeight: maxHeight,
                background: '#000',
                borderRadius: 'inherit',
                overflow: 'hidden',
            }}
        >
            <video
                ref={videoRef}
                controls={controls}
                muted={muted}
                loop={loop}
                poster={poster}
                playsInline
                preload={autoPlay ? 'metadata' : 'none'}
                disablePictureInPicture
                disableRemotePlayback
                controlsList="nodownload nofullscreen noremoteplayback"
                onPlay={onPlay}
                onEnded={onEnded}
                style={{
                    width: '100%',
                    height: '100%',
                    maxHeight: '100%',
                    display: 'block',
                    objectFit: 'contain',
                    objectPosition: 'center center',
                }}
            />

            {/* Simple quality badge for adaptive streams */}
            {!compact && isHlsActive && (
                <div
                    style={{
                        position: 'absolute',
                        top: '12px',
                        right: '12px',
                        zIndex: 10,
                        pointerEvents: 'none',
                        background: 'rgba(15, 23, 42, 0.72)',
                        color: '#fff',
                        fontSize: '11px',
                        fontWeight: 700,
                        padding: '5px 10px',
                        borderRadius: '999px',
                        letterSpacing: '0.04em',
                        backdropFilter: 'blur(8px)',
                    }}
                >
                    HD
                </div>
            )}

            {/* Error fallback message */}
            {error && (
                <div
                    style={{
                        position: 'absolute',
                        bottom: '8px',
                        left: '8px',
                        background: 'rgba(239,68,68,0.8)',
                        color: '#fff',
                        fontSize: '11px',
                        padding: '4px 8px',
                        borderRadius: '4px',
                    }}
                >
                    {error}
                </div>
            )}
        </div>
    );
};

export default HLSVideoPlayer;
